import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {InfoCircleFill} from 'react-bootstrap-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import './event-details.scss';
import {Form, Formik} from 'formik';
import SingleSelectDropdown from '../../../../../components/form/singleSelectDropdown/singleSelectDropdown';
import {
  abilityToReactEnum,
  alternativeControlsEnum,
  awarenessOfHazardEnum,
  controllabilityMapping,
  exposureEnum,
  riskLevelMapping,
  scoreParameterEnum,
  scoreParameterTooltipEnum,
  severityEnum,
} from '../../../../../utils/constants';
import {
  bulkAddFailureModeData,
  bulkUpdateFailureModeData,
  getFailureModeData,
} from '../../../../../redux/risk-assessment-core/riskAssessmentCoreSlice';
import {useDispatch, useSelector} from 'react-redux';
import {getFailureModeDataState} from '../../../../../redux/selectors/riskAssessmentCore';
import {useEffect, useState} from 'react';
import AddNote from '../../components/add-note/add-note';
import NoteButton from '../../../../../components/form/noteButton/noteButton';
import { toast } from 'react-toastify';
const PersonExposedTab = ({personType, eventId, riskAssessmentId}) => {
  const dispatch = useDispatch();
  const failureModeData = useSelector(getFailureModeDataState());

  const [formData, setFormData] = useState({
    severity: '',
    exposure: '',
    alternativeControls: '',
    awarenessOfHazard: '',
    abilityToReact: '',
  });

  const [controllability, setControllability] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  
  // Note states for each parameter
  const [noteStates, setNoteStates] = useState({
    severity: { value: '', loading: false },
    exposure: { value: '', loading: false },
    alternativeControls: { value: '', loading: false },
    awarenessOfHazard: { value: '', loading: false },
    abilityToReact: { value: '', loading: false },
  });

  // Track which note inputs should be shown (hidden by default, shown when "Add note" is clicked or if note exists)
  const [showNoteInputs, setShowNoteInputs] = useState({
    severity: false,
    exposure: false,
    alternativeControls: false,
    awarenessOfHazard: false,
    abilityToReact: false,
  });

  const fetchFailureModeData = (skipLoading = false) => {
    const queryParams = new URLSearchParams();
    queryParams.append('event_task', eventId);
    queryParams.append('parent_risk_assessment', riskAssessmentId);
    queryParams.append('person_exposed', personType);
    dispatch(getFailureModeData({ queryParams, skipLoading }));
  };

  const renderTooltip = content => (
    <Tooltip className="info-tooltip">{content}</Tooltip>
  );

  const updateOrAddScore = (key, newValue, dependentKey) => {
    const existingData = failureModeData?.find(
      (item) => item.score_parameter === scoreParameterEnum[key]
    );

    const dependentExistingData = failureModeData?.find(
      (item) => item.score_parameter === scoreParameterEnum[dependentKey]
    );
    
    if (existingData) {
      if (existingData.score !== newValue?.id) {
        let updatePayload = [{ id: existingData.id, score: newValue?.id || null }];
        if (dependentExistingData) {
          updatePayload.push({ id: dependentExistingData.id, score: null });
        }
        dispatch(
          bulkUpdateFailureModeData({
            payload: updatePayload,
            callback: response => {
              if (response.status === 200) {
                fetchFailureModeData(true);
              }
            },
          })
        );
      } else if (dependentExistingData && dependentExistingData.score !== null) {
        // If the main value hasn't changed but we need to clear dependent, still update
        dispatch(
          bulkUpdateFailureModeData({
            payload: [{ id: dependentExistingData.id, score: null }],
            callback: response => {
              if (response.status === 200) {
                fetchFailureModeData(true);
              }
            },
          })
        );
      }
    } else if (newValue?.id) {
      let addPayload = [{
        event_task: eventId,
        parent_risk_assessment: riskAssessmentId,
        person_exposed: personType,
        score_parameter: scoreParameterEnum[key],
        score: newValue?.id
      }];
      // If there's a dependent key that needs to be cleared, add it to payload
      if (dependentKey && dependentExistingData) {
        addPayload.push({ id: dependentExistingData.id, score: null });
      }
      dispatch(
        bulkAddFailureModeData({
          payload: addPayload,
          callback: response => {
            if (response.status === 201) {
              fetchFailureModeData(true);
            }
          },
        })
      );
    } else if (dependentKey && dependentExistingData && dependentExistingData.score !== null) {
      // If newValue is null/empty but we need to clear dependent
      dispatch(
        bulkUpdateFailureModeData({
          payload: [{ id: dependentExistingData.id, score: null }],
          callback: response => {
            if (response.status === 200) {
              fetchFailureModeData(true);
            }
          },
        })
      );
    }
  };

  // Note handling functions
  const updateNote = (parameter, value) => {
    setNoteStates(prev => ({
      ...prev,
      [parameter]: {
        ...prev[parameter],
        value: value
      }
    }));
  };

  // Handler for AddNote component's setFiledValue
  // This prevents clearing saved notes when closing
  const handleNoteFieldChange = (parameter, value) => {
    // If trying to clear (empty string), check if there's a saved note
    // if (value === '') {
    //   const existingData = failureModeData?.find(
    //     (item) => item.score_parameter === scoreParameterEnum[parameter]
    //   );
    //   // If there's a saved note, restore it instead of clearing
    //   if (existingData?.note) {
    //     updateNote(parameter, existingData.note);
    //     return;
    //   }
    // }
    // Otherwise, update normally
    updateNote(parameter, value);
  };

  // Handler to restore saved note when reopening
  const handleShowNoteInput = (parameter, show) => {
    if (show) {
      // When opening, restore the saved note value from failureModeData
      const existingData = failureModeData?.find(
        (item) => item.score_parameter === scoreParameterEnum[parameter]
      );
      if (existingData?.note) {
        updateNote(parameter, existingData.note);
      } else {
        // If no saved note, clear any unsaved changes
        updateNote(parameter, '');
      }
    }
    setShowNoteInputs(prev => ({ ...prev, [parameter]: show }));
  };

  const saveNote = (parameter) => {
    const existingData = failureModeData?.find(
      (item) => item.score_parameter === scoreParameterEnum[parameter]
    );
    if(noteStates[parameter].value?.trim() === '') {
      toast.error('Note cannot be empty');
      return;
    }

    setNoteStates(prev => ({
      ...prev,
      [parameter]: {
        ...prev[parameter],
        loading: true
      }
    }));


    if (existingData) {
      // Update existing note
      dispatch(
        bulkUpdateFailureModeData({
          payload: [{ id: existingData.id, note: noteStates[parameter].value }],
          callback: response => {
            setNoteStates(prev => ({
              ...prev,
              [parameter]: {
                ...prev[parameter],
                loading: false
              }
            }));
            if (response.status === 200) {
              fetchFailureModeData(true);
              toast.success('Note saved successfully');
            }
          },
        })
      );
    } else {
      // Create new assessment with note
      dispatch(
        bulkAddFailureModeData({
          payload: [{
            event_task: eventId,
            parent_risk_assessment: riskAssessmentId,
            person_exposed: personType,
            score_parameter: scoreParameterEnum[parameter],
            score: null,
            note: noteStates[parameter].value
          }],
          callback: response => {
            setNoteStates(prev => ({
              ...prev,
              [parameter]: {
                ...prev[parameter],
                loading: false
              }
            }));
            if (response.status === 201) {
              fetchFailureModeData(true);
              toast.success('Note saved successfully');
            }
          },
        })
      );
    }
  };

  useEffect(() => {
    fetchFailureModeData();
  }, []);

  useEffect(() => {
    if (failureModeData && failureModeData.length > 0) {
      const scores = {
        severity:
          failureModeData.find(
            item => item.score_parameter === scoreParameterEnum.severity,
          )?.score || '',
        exposure:
          failureModeData.find(
            item => item.score_parameter === scoreParameterEnum.exposure,
          )?.score || '',
        alternativeControls:
          failureModeData.find(
            item =>
              item.score_parameter === scoreParameterEnum.alternativeControls,
          )?.score || '',
        awarenessOfHazard:
          failureModeData.find(
            item =>
              item.score_parameter === scoreParameterEnum.awarenessOfHazard,
          )?.score || '',
        abilityToReact:
          failureModeData.find(
            item => item.score_parameter === scoreParameterEnum.abilityToReact,
          )?.score || '',
      };
      let controllability = '';
      // For AC0, both awarenessOfHazard and abilityToReact are hidden/cleared
      // AC0 always results in C3 regardless of abilityToReact value
      // For AC1+, both awarenessOfHazard and abilityToReact are required
      if (scores.alternativeControls) {
        // Check if AC0 (first item in alternativeControlsEnum)
        const isAC0 = scores.alternativeControls === alternativeControlsEnum[0]?.id;
        
        if (isAC0) {
          // AC0: Both fields are hidden, but controllability is always C3
          // All AC0 mappings (AC0-AR0, AC0-AR1, AC0-AR2, AC0-AR3) result in C3
          controllability = 'C3';
        } else {
          // AC1+ requires both awarenessOfHazard and abilityToReact
          if (scores.awarenessOfHazard && scores.abilityToReact) {
            const controlKey = `${scores.alternativeControls}-${scores.awarenessOfHazard}-${scores.abilityToReact}`;
            controllability = controlKey ? controllabilityMapping[controlKey] : '';
          }
        }
      }
      setControllability(controllability);
      if (scores.severity && controllability) {
        const riskKey = `${scores.severity}${
          scores.exposure ? '-' + scores.exposure : ''
        }-${controllability}`;
        const riskLevel = riskKey ? riskLevelMapping[riskKey] : '';
        setRiskLevel(riskLevel);
      } else {
        setRiskLevel('');
      }
      setFormData(prevState => ({
        ...prevState,
        severity: scores.severity
          ? severityEnum.find(item => item.id === scores.severity)
          : '',
        exposure: scores.exposure
          ? exposureEnum.find(item => item.id === scores.exposure)
          : '',
        alternativeControls: scores.alternativeControls
          ? alternativeControlsEnum.find(
              item => item.id === scores.alternativeControls,
            )
          : '',
        awarenessOfHazard: scores.awarenessOfHazard
          ? awarenessOfHazardEnum.find(
              item => item.id === scores.awarenessOfHazard,
            )
          : '',
        abilityToReact: scores.abilityToReact
          ? abilityToReactEnum.find(item => item.id === scores.abilityToReact)
          : '',
      }));

      // Load existing notes
      const parameters = ['severity', 'exposure', 'alternativeControls', 'awarenessOfHazard', 'abilityToReact'];
      const newNoteStates = {};
      const newShowNoteInputs = {};
      parameters.forEach(param => {
        const existingData = failureModeData.find(
          item => item.score_parameter === scoreParameterEnum[param]
        );
        const noteValue = existingData?.note || '';
        newNoteStates[param] = {
          value: noteValue,
          loading: false
        };
        // Show note input if note already exists, otherwise hide it
        newShowNoteInputs[param] = !!noteValue;
      });
      setNoteStates(newNoteStates);
      setShowNoteInputs(newShowNoteInputs);
    } else {
      setFormData({
        severity: '',
        exposure: '',
        alternativeControls: '',
        awarenessOfHazard: '',
        abilityToReact: '',
      });
      setControllability('');
      setRiskLevel('');
      setNoteStates({
        severity: { value: '', loading: false },
        exposure: { value: '', loading: false },
        alternativeControls: { value: '', loading: false },
        awarenessOfHazard: { value: '', loading: false },
        abilityToReact: { value: '', loading: false },
      });
      setShowNoteInputs({
        severity: false,
        exposure: false,
        alternativeControls: false,
        awarenessOfHazard: false,
        abilityToReact: false,
      });
    }
  }, [failureModeData]);

  return (
    <Formik initialValues={formData} enableReinitialize>
      {({values, setFieldValue}) => (
        <Form>
          <SingleSelectDropdown
            label={
              <span className="d-flex align-items-center gap-1">
                Severity
                <OverlayTrigger
                  placement="right"
                  overlay={renderTooltip(scoreParameterTooltipEnum.severity)}>
                  <InfoCircleFill size={16} className="info-icon" />
                </OverlayTrigger>
              </span>
            }
            listItems={severityEnum}
            name="severity"
            value={values['severity']}
            handleSelectedUsersChange={val => {
              setFieldValue('severity', val);
              // If severity is S0, set exposure to null and disable it
              if (val.id === severityEnum[0].id) {
                setFieldValue('exposure', '');
                updateOrAddScore('severity', val, 'exposure');
              } else {
                updateOrAddScore('severity', val);
              }
            }}
          />
          
                {/* Severity Note - Show when severity is selected */}
                {!!values['severity'] && (
                  <div style={{ marginTop: '10px' }}>
                    <AddNote
                      showNoteInput={showNoteInputs.severity}
                      setShowNoteInput={(show) => handleShowNoteInput('severity', show)}
                      name="severity_note"
                      value={noteStates.severity.value}
                      setFiledValue={(name, value) => handleNoteFieldChange('severity', value)}
                      readOnly={false}
                    />
                    {showNoteInputs.severity && (
                      <div style={{ marginTop: '10px' }}>
                        <NoteButton
                          parameter="severity"
                          loading={noteStates.severity.loading}
                          onSave={saveNote}
                        />
                      </div>
                    )}
                  </div>
                )}
          {values['severity']?.id !== severityEnum[0].id && (
            <>
              <SingleSelectDropdown
                label={
                  <span className="d-flex align-items-center gap-1">
                    Exposure
                    <OverlayTrigger
                      placement="right"
                      overlay={renderTooltip(scoreParameterTooltipEnum.exposure)}>
                      <InfoCircleFill size={16} className="info-icon" />
                    </OverlayTrigger>
                  </span>
                }
                listItems={exposureEnum}
                name="exposure"
                value={values['exposure']}
                handleSelectedUsersChange={val => {
                  setFieldValue('exposure', val);
                  updateOrAddScore('exposure', val);
                }}
              />
              
                    {/* Exposure Note - Show when exposure is selected */}
                    {!!values['exposure'] && (
                      <div style={{ marginTop: '10px' }}>
                        <AddNote
                          showNoteInput={showNoteInputs.exposure}
                          setShowNoteInput={(show) => handleShowNoteInput('exposure', show)}
                          name="exposure_note"
                          value={noteStates.exposure.value}
                          setFiledValue={(name, value) => handleNoteFieldChange('exposure', value)}
                          readOnly={false}
                        />
                        {showNoteInputs.exposure && (
                          <div style={{ marginTop: '10px' }}>
                            <NoteButton
                              parameter="exposure"
                              loading={noteStates.exposure.loading}
                              onSave={saveNote}
                            />
                          </div>
                        )}
                      </div>
                    )}
            </>
          )}
          <SingleSelectDropdown
            label={
              <span className="d-flex align-items-center gap-1">
                Alternative Controls
                <OverlayTrigger
                  placement="right"
                  overlay={renderTooltip(scoreParameterTooltipEnum.alternativeControls)}>
                  <InfoCircleFill size={16} className="info-icon" />
                </OverlayTrigger>
              </span>
            }
            listItems={alternativeControlsEnum}
            name="alternativeControls"
            value={values['alternativeControls']}
            handleSelectedUsersChange={val => {
              setFieldValue('alternativeControls', val);
              // If alternative controls is AC0, clear both awareness of hazard and ability to react
              if (val.id === alternativeControlsEnum[0].id) {
                setFieldValue('awarenessOfHazard', '');
                setFieldValue('abilityToReact', '');
                // Clear both dependent fields in backend
                const awarenessData = failureModeData?.find(
                  (item) => item.score_parameter === scoreParameterEnum['awarenessOfHazard']
                );
                const abilityData = failureModeData?.find(
                  (item) => item.score_parameter === scoreParameterEnum['abilityToReact']
                );
                const alternativeControlsData = failureModeData?.find(
                  (item) => item.score_parameter === scoreParameterEnum['alternativeControls']
                );
                
                let updatePayload = [];
                
                // Update or add alternativeControls
                if (alternativeControlsData) {
                  if (alternativeControlsData.score !== val?.id) {
                    updatePayload.push({ id: alternativeControlsData.id, score: val?.id || null });
                  }
                } else if (val?.id) {
                  // Will be handled separately if needed
                }
                
                // Clear awarenessOfHazard
                if (awarenessData && awarenessData.score !== null) {
                  updatePayload.push({ id: awarenessData.id, score: null });
                }
                
                // Clear abilityToReact
                if (abilityData && abilityData.score !== null) {
                  updatePayload.push({ id: abilityData.id, score: null });
                }
                
                if (updatePayload.length > 0) {
                  dispatch(
                    bulkUpdateFailureModeData({
                      payload: updatePayload,
                      callback: response => {
                        if (response.status === 200) {
                          fetchFailureModeData(true);
                        }
                      },
                    })
                  );
                } else if (val?.id && !alternativeControlsData) {
                  // If alternativeControls doesn't exist, create it
                  updateOrAddScore('alternativeControls', val);
                }
              } else {
                updateOrAddScore('alternativeControls', val);
              }
            }}
          />
          
          {/* Alternative Controls Note - Show when alternative controls is selected */}
          {!!values['alternativeControls'] && (
            <div style={{ marginTop: '10px' }}>
              <AddNote
                showNoteInput={showNoteInputs.alternativeControls}
                setShowNoteInput={(show) => handleShowNoteInput('alternativeControls', show)}
                name="alternativeControls_note"
                value={noteStates.alternativeControls.value}
                setFiledValue={(name, value) => handleNoteFieldChange('alternativeControls', value)}
                readOnly={false}
              />
              {showNoteInputs.alternativeControls && (
                <div style={{ marginTop: '10px' }}>
                  <NoteButton
                    parameter="alternativeControls"
                    loading={noteStates.alternativeControls.loading}
                    onSave={saveNote}
                  />
                </div>
              )}
            </div>
          )}
          {values['alternativeControls']?.id !== alternativeControlsEnum[0].id && (
            <>
              <SingleSelectDropdown
                label={
                  <span className="d-flex align-items-center gap-1">
                    Awareness of Hazard
                    <OverlayTrigger
                      placement="right"
                      overlay={renderTooltip(scoreParameterTooltipEnum.awarenessOfHazard)}>
                      <InfoCircleFill size={16} className="info-icon" />
                    </OverlayTrigger>
                  </span>
                }
                listItems={awarenessOfHazardEnum}
                name="awarenessOfHazard"
                value={values['awarenessOfHazard']}
                handleSelectedUsersChange={val => {
                  setFieldValue('awarenessOfHazard', val);
                  updateOrAddScore('awarenessOfHazard', val);
                }}
              />
              
              {/* Awareness of Hazard Note - Show when awareness of hazard is selected */}
              {!!values['awarenessOfHazard'] && (
                <div style={{ marginTop: '10px' }}>
                  <AddNote
                    showNoteInput={showNoteInputs.awarenessOfHazard}
                    setShowNoteInput={(show) => handleShowNoteInput('awarenessOfHazard', show)}
                    name="awarenessOfHazard_note"
                    value={noteStates.awarenessOfHazard.value}
                    setFiledValue={(name, value) => handleNoteFieldChange('awarenessOfHazard', value)}
                    readOnly={false}
                  />
                  {showNoteInputs.awarenessOfHazard && (
                    <div style={{ marginTop: '10px' }}>
                      <NoteButton
                        parameter="awarenessOfHazard"
                        loading={noteStates.awarenessOfHazard.loading}
                        onSave={saveNote}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Ability to React is only shown when Alternative Controls is NOT AC0 */}
          {values['alternativeControls']?.id !== alternativeControlsEnum[0].id && (
            <>
              <SingleSelectDropdown
                label={
                  <span className="d-flex align-items-center gap-1">
                    Ability to React
                    <OverlayTrigger
                      placement="right"
                      overlay={renderTooltip(scoreParameterTooltipEnum.abilityToReact)}>
                      <InfoCircleFill size={16} className="info-icon" />
                    </OverlayTrigger>
                  </span>
                }
                listItems={abilityToReactEnum}
                name="abilityToReact"
                value={values['abilityToReact']}
                handleSelectedUsersChange={val => {
                  setFieldValue('abilityToReact', val);
                  updateOrAddScore('abilityToReact', val);
                }}
              />
              
                  {/* Ability to React Note - Show when ability to react is selected */}
                  {!!values['abilityToReact'] && (
                    <div style={{ marginTop: '10px' }}>
                      <AddNote
                        showNoteInput={showNoteInputs.abilityToReact}
                        setShowNoteInput={(show) => handleShowNoteInput('abilityToReact', show)}
                        name="abilityToReact_note"
                        value={noteStates.abilityToReact.value}
                        setFiledValue={(name, value) => handleNoteFieldChange('abilityToReact', value)}
                        readOnly={false}
                      />
                      {showNoteInputs.abilityToReact && (
                        <div style={{ marginTop: '10px' }}>
                          <NoteButton
                            parameter="abilityToReact"
                            loading={noteStates.abilityToReact.loading}
                            onSave={saveNote}
                          />
                        </div>
                      )}
                    </div>
                  )}
            </>
          )}

          <div className="risk-info mb-4">
            <div className="mb-2">
              <strong>Controllability:</strong> <strong>{controllability || 'N/A'}</strong>
            </div>
            <div>
              <strong>Risk Level(MPLr):</strong> <strong>{riskLevel || 'N/A'}</strong>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PersonExposedTab;
